import { gql, type TypedDocumentNode, useApolloClient } from '@apollo/client';
import { RiAddLine, RiMapPinFill } from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { AgText } from 'src/components/ui';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';
import { DeferredRetry } from 'src/modules/entities/deferred';
import { appLinks } from 'src/routing/appLinks';
import { useRouteLoaderQuery } from 'src/routing/useRouteLoaderQuery';
import formatAddress from 'src/utils/address/formatAddress';
import type { WarehouseCardsPageQuery, WarehouseCardsPageQueryVariables } from 'types/graphql';

import WarehouseModal from './WarehouseModal';

/** The Warehouses tab's bounded read (P7-R35): the same card fields `WarehouseListQuery` read, one page at a time. */
export const WAREHOUSE_CARDS_PAGE: TypedDocumentNode<WarehouseCardsPageQuery, WarehouseCardsPageQueryVariables> = gql`
  query WarehouseCardsPageQuery($input: StandardPageInput) {
    warehousePage(input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          tagKeys
          place {
            id
            description
            streetAddress1
            streetAddress2
            city
            state
            postalCode
            addressCountry
            fullAddress
          }
        }
      }
    }
  }
`;

/** Oldest first, like the unordered list read it replaces; the largest org holds 26 (R-WH1). */
export const WAREHOUSE_CARDS_FIRST_PAGE = {
  input: { first: 60, sortItems: [{ field: 'id', sort: 'asc' }] },
} satisfies WarehouseCardsPageQueryVariables;

type CardsPage = WarehouseCardsPageQuery['warehousePage'];
type WarehouseCard = NonNullable<NonNullable<CardsPage['edges']>[number]>['node'];

const cardsOf = (page: CardsPage | undefined): WarehouseCard[] =>
  page?.edges?.flatMap((edge) => (edge ? [edge.node] : [])) ?? [];

/** Later pages, read on "Show more"; they belong to the first page they followed. */
type MorePages = Readonly<{ after: CardsPage; cards: WarehouseCard[]; pageInfo: CardsPage['pageInfo'] }>;

function SkeletonCard() {
  return (
    <div className="h-full card animate-pulse p-6">
      <div className="mb-4 h-4 w-3/4 rounded bg-neutral-3" />
      <div className="h-3 w-1/2 rounded bg-neutral-3" />
    </div>
  );
}

export default function WarehousesGrid() {
  const navigate = useNavigate();
  const client = useApolloClient();
  const { data, loading, error, refetch, reader } = useRouteLoaderQuery(
    WAREHOUSE_CARDS_PAGE,
    WAREHOUSE_CARDS_FIRST_PAGE
  );
  const firstPage = data?.warehousePage;
  const [storedMore, setMore] = useState<MorePages | null>(null);
  const more = storedMore && firstPage && storedMore.after === firstPage ? storedMore : null;
  const warehouses = [...cardsOf(firstPage), ...(more?.cards ?? [])];
  const pageInfo = more ? more.pageInfo : firstPage?.pageInfo;
  const [loadingMore, setLoadingMore] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);

  const showMore = async () => {
    const after = pageInfo?.endCursor;
    if (!firstPage || !after || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data: next } = await client.query({
        query: WAREHOUSE_CARDS_PAGE,
        variables: { input: { ...WAREHOUSE_CARDS_FIRST_PAGE.input, after } },
      });
      setMore({
        after: firstPage,
        cards: [...(more?.cards ?? []), ...cardsOf(next.warehousePage)],
        pageInfo: next.warehousePage.pageInfo,
      });
    } catch (error) {
      notifyUnlessSurfaced(error, 'Unable to load more warehouses.');
    } finally {
      setLoadingMore(false);
    }
  };

  if (error) {
    return (
      <>
        {reader}
        <DeferredRetry
          message="Unable to load warehouses."
          actionLabel="Retry warehouses"
          onRetry={() => refetch().catch(() => undefined)}
        />
      </>
    );
  }

  if (loading) {
    return (
      <>
        {reader}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </>
    );
  }

  return (
    <>
      {reader}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {warehouses.map((warehouse) => (
          <div
            key={warehouse.id}
            role="button"
            tabIndex={0}
            className="card cursor-pointer transition-shadow hover:shadow-md"
            onClick={() => void navigate(appLinks.warehouseDetails({ warehouseId: warehouse.id }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                void navigate(appLinks.warehouseDetails({ warehouseId: warehouse.id }));
              }
            }}
          >
            <div className="flex flex-col gap-2 p-6">
              <AgText size="16" weight="semibold">
                {warehouse.place.description || 'Unnamed Warehouse'}
              </AgText>
              <div className="flex items-center gap-2">
                <RiMapPinFill size={20} />
                <AgText size="14" className="text-neutral-8">
                  {formatAddress(warehouse.place)}
                </AgText>
              </div>
            </div>
          </div>
        ))}

        {pageInfo?.hasNextPage && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Show more warehouses"
            aria-busy={loadingMore}
            className="card cursor-pointer transition-shadow hover:shadow-md"
            onClick={() => void showMore()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                void showMore();
              }
            }}
          >
            <div className="flex h-full items-center gap-2 p-6">
              <AgText size="14" weight="semibold" className="uppercase tracking-wider text-brand-text">
                SHOW MORE WAREHOUSES
              </AgText>
            </div>
          </div>
        )}

        {/* Add New Warehouse Card */}
        <div
          role="button"
          tabIndex={0}
          className="card cursor-pointer transition-shadow hover:shadow-md"
          onClick={() => setAddModalOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setAddModalOpen(true);
            }
          }}
        >
          <div className="flex h-full items-center gap-2 p-6">
            <RiAddLine size={20} />
            <AgText size="14" weight="semibold" className="uppercase tracking-wider text-brand-text">
              ADD WAREHOUSE
            </AgText>
          </div>
        </div>
      </div>

      <WarehouseModal open={addModalOpen} onClose={() => setAddModalOpen(false)} />
    </>
  );
}
